import React, { useRef, useState } from 'react';
import type { AdminIndustry } from '../../../services/adminService';
import { AdminFieldTw, AdminModalTw, btnSecondary, selectClass } from './adminUi';
import { RECORD_SOURCE_PRESETS } from './RecordDrawer';
import RecordExcelUploadPreview from './recordExcel/RecordExcelUploadPreview';
import { parseRecordExcelFile, type RecordExcelParseResult } from './recordExcel/recordExcelParser';

export type RecordExcelUploadDraft = {
  industryId: number;
  source: string;
};

type Props = {
  industries: AdminIndustry[];
  onClose: () => void;
  canEditRecords?: boolean;
  onImported?: () => void;
};

export default function RecordExcelUploadModal({ industries, onClose, canEditRecords = false, onImported }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [industryId, setIndustryId] = useState('');
  const [source, setSource] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parseResult, setParseResult] = useState<RecordExcelParseResult | null>(null);

  const industrySelected = industryId !== '';
  const sourceSelected = source !== '';
  const readyForFile = industrySelected && sourceSelected;

  const selectedIndustry = industries.find((item) => String(item.id) === industryId);
  const selectedSource = RECORD_SOURCE_PRESETS.find((preset) => preset.value === source);

  const resetFile = () => {
    setParseResult(null);
    setParseError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setParseError(null);
    setParsing(true);
    try {
      const buffer = await file.arrayBuffer();
      const result = await parseRecordExcelFile(buffer, file.name);
      setParseResult(result);
    } catch (err) {
      setParseResult(null);
      setParseError(err instanceof Error ? err.message : 'Не удалось разобрать файл');
    } finally {
      setParsing(false);
    }
  };

  return (
    <AdminModalTw
      title="Загрузка Excel"
      onClose={onClose}
      size={parseResult ? 'xl' : 'wide'}
      footer={(
        <button type="button" className={btnSecondary} onClick={onClose}>
          Закрыть
        </button>
      )}
    >
      {!parseResult ? (
        <>
          <p className="mb-5 text-sm text-dc-muted">
            Укажите контекст импорта и загрузите файл в формате «Загрузочный файл.xlsx» (лист «Данные»).
          </p>

          <ol className="space-y-5">
            <li>
              <AdminFieldTw label="1. Выберите индустрию" dbField="industries.id" required>
                <select
                  className={`${selectClass} dc-select-full`}
                  value={industryId}
                  onChange={(e) => {
                    setIndustryId(e.target.value);
                    setSource('');
                    resetFile();
                  }}
                >
                  <option value="">Выберите индустрию</option>
                  {industries.map((item) => (
                    <option key={item.id} value={item.id}>{item.name}</option>
                  ))}
                </select>
              </AdminFieldTw>
            </li>

            <li className={industrySelected ? '' : 'opacity-50'}>
              <AdminFieldTw
                label="2. Выберите источник данных"
                dbField="compensation_records.source"
                required
                hint={!industrySelected ? 'Сначала выберите индустрию' : undefined}
              >
                <select
                  className={`${selectClass} dc-select-full`}
                  value={source}
                  onChange={(e) => {
                    setSource(e.target.value);
                    resetFile();
                  }}
                  disabled={!industrySelected}
                >
                  <option value="">Выберите источник</option>
                  {RECORD_SOURCE_PRESETS.map((preset) => (
                    <option key={preset.value} value={preset.value}>{preset.label}</option>
                  ))}
                </select>
              </AdminFieldTw>
            </li>

            <li className={readyForFile ? '' : 'opacity-50'}>
              <AdminFieldTw label="3. Excel-файл" hint={!readyForFile ? 'Заполните шаги 1 и 2' : undefined}>
                <div
                  className={`rounded-lg border-2 border-dashed px-4 py-6 ${
                    readyForFile ? 'border-slate-300 bg-slate-50' : 'border-slate-200 bg-slate-50/60'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    disabled={!readyForFile || parsing}
                    onChange={handleFileChange}
                    className="block w-full text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-emerald-600 file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-emerald-700 disabled:opacity-50"
                    aria-label="Выбрать Excel-файл"
                  />
                  {parsing && <p className="mt-2 text-xs text-dc-muted">Разбор файла…</p>}
                  {parseError && <p className="mt-2 text-xs text-red-600">{parseError}</p>}
                  {readyForFile && !parsing && !parseError && (
                    <p className="mt-2 text-xs text-dc-muted">
                      Ожидается лист «Данные», заголовки в строке 6, данные с строки 7.
                    </p>
                  )}
                </div>
              </AdminFieldTw>
            </li>
          </ol>
        </>
      ) : (
        selectedIndustry && selectedSource && (
          <RecordExcelUploadPreview
            parseResult={parseResult}
            uploadContext={{
              industryId: selectedIndustry.id,
              industryName: selectedIndustry.name,
              source: selectedSource.value,
              sourceLabel: selectedSource.label,
            }}
            canEditRecords={canEditRecords}
            onReset={resetFile}
            onImported={onImported}
          />
        )
      )}
    </AdminModalTw>
  );
}
